import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChartLine, Check, X } from 'shared/icons';
import api from '../../../services/api';
import { FormatDurationSec } from '../../../utils/format';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import { DateTimeText, Id } from '../../ui/Mono';
import { SkeletonList } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import { FormatLocationPlain } from '../collector/collectorScan';
import { ExtractListRows } from '../payload';
import RouteComparator from './RouteComparator';
import {
    CONSOLE_STATUS_CANCELED,
    CONSOLE_STATUS_READY,
    HandoffSourceOf,
    IndexServiceOrderItemsById,
    ItemIsWaitingForHandoff,
    ItemLocationOf,
    PauseDurationSeconds,
    RouteItemsOfOrder,
    SERVICE_ORDER_STATUS,
    ServiceOrderItemStatusVariant,
    ServiceOrderStatusVariant,
    ServiceOrderTiming,
} from './serviceOrderModel';

/**
 * FICHA da OS: itens na ordem da rota, pausas, SLA e o par quando existe.
 *
 * ── O SLA, com as três medidas separadas ────────────────────────────────────
 * "Tempo de execução" é `finished_at − accepted_at − Σ pausas`. Mostrar só ele
 * esconde metade da história de uma OS que ficou parada meia hora, então a
 * ficha mostra os três números lado a lado (execução, total desde o aceite e
 * quanto ficou pausada) — e nenhum deles é recalculado aqui: os três vêm do
 * `sla` do DTO, que sai de uma função pura testada no backend.
 *
 * ⚠️ OS CANCELADA: o backend carimba `finished_at` no cancelamento para o
 * número não correr para sempre, mas isso NÃO é tempo de trabalho concluído.
 * Na ficha o bloco inteiro troca de rótulo para "Cancelada" e mostra o tempo
 * decorrido — nunca sob a palavra "execução".
 *
 * ── O par: HANDOFF, não `seq` (M5 r1, regra 4) ──────────────────────────────
 * As OSs de uma jornada dividida compartilham `group_id`, mas o par de um item
 * NÃO é mais "o item de igual `seq` na OS irmã": desde 11/08 uma jornada pode
 * pôr duas pernas verticais na MESMA OS, e a `seq` voltou a ser só a ordem
 * dentro da OS. Casar por `seq` hoje mostraria pares ERRADOS.
 *
 * O vínculo é a coluna `handoff_from_item_id`: cada item aponta para o item que
 * ENTREGA o pallet no endereço onde ele começa. A ficha busca as OSs do grupo,
 * indexa TODOS os itens por id e resolve o ponteiro — é o que explica ao
 * Supervisor por que um operador está parado esperando, e de quem.
 */

const BLANK = <span className="text-mono-400">—</span>;

const SummaryRow = ({ term, children }) => (
    <div className="flex items-baseline justify-between gap-3 border-b border-mono-100 py-1.5 last:border-0 dark:border-mono-900">
        <dt className="shrink-0 text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">{term}</dt>
        <dd className="min-w-0 text-right text-sm text-black dark:text-white">{children}</dd>
    </div>
);

const ServiceOrderDetailModal = ({ serviceOrderId, label, canManage = false, onClose, onChanged }) => {
    const [order, setOrder] = useState(null);
    // As OUTRAS OSs da jornada. Lista (não "a irmã"): a divisão padrão dá duas,
    // mas o grupo é o que amarra a jornada — presumir exatamente duas seria
    // outra suposição de modelo, e é justamente isso que o M5 r1 desfez.
    const [siblings, setSiblings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmingCancel, setConfirmingCancel] = useState(false);
    const [showRoute, setShowRoute] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const detail = await api.getWarehouseServiceOrder(serviceOrderId);
            setOrder(detail);

            // As outras OSs do grupo. A ficha de cada uma é buscada INTEIRA
            // porque o vínculo é por ITEM (`handoff_from_item_id` aponta um id
            // de item, e a listagem não traz itens).
            if (detail?.group_id) {
                const groupPayload = await api.getWarehouseServiceOrders({ group_id: detail.group_id, limit: 10 });
                const others = ExtractListRows(groupPayload, ['service_orders']).filter((row) => row.id !== detail.id);
                setSiblings(await Promise.all(others.map((row) => api.getWarehouseServiceOrder(row.id))));
            } else {
                setSiblings([]);
            }
        } catch (error) {
            setLoadError(error?.message || label('wms.common.load_error'));
            setOrder(null);
        } finally {
            setLoading(false);
        }
    }, [serviceOrderId, label]);

    useEffect(() => {
        load();
    }, [load]);

    // Todos os itens da jornada por id (esta OS + as do grupo): é neste índice
    // que o `handoff_from_item_id` de cada item é resolvido.
    const itemsById = useMemo(
        () => IndexServiceOrderItemsById([order, ...siblings].filter(Boolean)),
        [order, siblings],
    );
    const timing = order ? ServiceOrderTiming(order) : null;
    const isCanceled = order?.status === SERVICE_ORDER_STATUS.CANCELED;
    const routeItems = useMemo(() => RouteItemsOfOrder(order), [order]);

    const patchStatus = async (status) => {
        setBusy(true);
        setActionError(null);
        try {
            await api.updateWarehouseServiceOrder(serviceOrderId, { status });
            setConfirmingCancel(false);
            await load();
            onChanged?.();
        } catch (error) {
            setActionError(error?.message || label('wms.common.load_error'));
        } finally {
            setBusy(false);
        }
    };

    const itemColumns = useMemo(
        () => [
            {
                key: 'seq',
                label: '#',
                sortable: false,
                getValue: (row) => row.seq,
                render: (row) => <Id value={row.seq} className="text-xs" />,
            },
            {
                key: 'status',
                label: label('wms.field.status'),
                sortable: false,
                getValue: (row) => label(`wms.service_order_item.status.${row.status}`),
                render: (row) => (
                    <div className="space-y-1">
                        <Badge variant={ServiceOrderItemStatusVariant(row.status)}>
                            {label(`wms.service_order_item.status.${row.status}`)}
                        </Badge>
                        {row.status === 'SKIPPED_ALERT' ? (
                            <p className="max-w-[220px] text-[11px] text-mono-500 dark:text-mono-400">
                                {label('wms.service_order_item.skipped_help')}
                            </p>
                        ) : null}
                    </div>
                ),
            },
            {
                key: 'lpn',
                label: label('wms.collector.lpn_label'),
                sortable: false,
                getValue: (row) => row.lpn_code || '',
                render: (row) => (row.lpn_code ? <Id value={row.lpn_code} className="text-xs" /> : BLANK),
            },
            {
                key: 'from',
                label: label('wms.service_order_item.from'),
                sortable: false,
                getValue: (row) => row.from_location_code || '',
                render: (row) => (
                    <div className="min-w-0">
                        <Id value={row.from_location_code} className="text-xs" />
                        <p className="truncate text-[11px] text-mono-500 dark:text-mono-400">
                            {FormatLocationPlain(ItemLocationOf(row, 'from'), { label })}
                        </p>
                    </div>
                ),
            },
            {
                key: 'to',
                label: label('wms.service_order_item.to'),
                sortable: false,
                getValue: (row) => row.to_location_code || '',
                // DESTINO LIVRE: o item de pallet inteiro nasceu sem destino e o
                // operador escolhe no bipe. Depois de feito, o destino escolhido
                // fica gravado no item e aparece aqui como qualquer outro.
                render: (row) =>
                    row.to_location_code ? (
                        <div className="min-w-0">
                            <Id value={row.to_location_code} className="text-xs" />
                            <p className="truncate text-[11px] text-mono-500 dark:text-mono-400">
                                {FormatLocationPlain(ItemLocationOf(row, 'to'), { label })}
                            </p>
                        </div>
                    ) : (
                        <span className="text-xs italic text-mono-500 dark:text-mono-400">
                            {label('wms.service_order_item.to_free')}
                        </span>
                    ),
            },
            // A coluna da TROCA DE MÃO só existe quando a jornada foi dividida:
            // numa OS que faz tudo sozinha ela seria uma coluna de travessões
            // pedindo explicação.
            ...(siblings.length > 0
                ? [
                      {
                          key: 'handoff',
                          label: label('wms.service_order.handoff_from'),
                          sortable: false,
                          getValue: (row) => HandoffSourceOf(row, itemsById)?.item?.status || '',
                          render: (row) => {
                              // O vínculo é o PONTEIRO do item, não a `seq`
                              // (regra 4): sem ele, este item não espera
                              // ninguém — está com a carga na mão.
                              const source = HandoffSourceOf(row, itemsById);
                              if (!source) {
                                  return BLANK;
                              }
                              return (
                                  <div className="space-y-1">
                                      <div className="flex flex-wrap items-center gap-1.5">
                                          <Id value={source.order?.code} className="text-[11px]" />
                                          <Badge variant={ServiceOrderItemStatusVariant(source.item.status)}>
                                              {label(`wms.service_order_item.status.${source.item.status}`)}
                                          </Badge>
                                      </div>
                                      {/* ONDE a carga troca de mão: é o endereço
                                          que o operador adiantado vai olhar. */}
                                      <p className="max-w-[200px] text-[11px] text-mono-500 dark:text-mono-400">
                                          {source.item.to_location_code || row.from_location_code}
                                      </p>
                                      {ItemIsWaitingForHandoff(row, source.item) ? (
                                          <p className="max-w-[200px] text-[11px] text-warning-700 dark:text-warning-400">
                                              {label('wms.service_order.waiting_handoff')}
                                          </p>
                                      ) : null}
                                  </div>
                              );
                          },
                      },
                  ]
                : []),
        ],
        [label, itemsById, siblings],
    );

    // O código JÁ começa com "OS" (§4: `OS` + YYMMDD + SEQ3) — prefixá-lo com o
    // rótulo produziria "OS OS260810005".
    const modalTitle = order ? order.code : label('wms.service_orders.title');
    const canRelease = canManage && order?.status === SERVICE_ORDER_STATUS.BACKLOG;
    const canCancel =
        canManage &&
        order &&
        ![SERVICE_ORDER_STATUS.DONE, SERVICE_ORDER_STATUS.CANCELED].includes(order.status);

    return (
        <Modal
            isOpen
            onClose={onClose}
            size="4xl"
            title={modalTitle}
            actions={
                <div className="flex w-full flex-wrap items-center gap-2">
                    {canRelease ? (
                        <Button variant="primary" loading={busy} onClick={() => patchStatus(CONSOLE_STATUS_READY)}>
                            <Check size={16} className="mr-1.5" />
                            {label('wms.kanban.approve')}
                        </Button>
                    ) : null}

                    {/* Cancelar é destrutivo e irreversível (CANCELED é
                        terminal): confirma no lugar, sem abrir modal sobre
                        modal. */}
                    {canCancel ? (
                        confirmingCancel ? (
                            <>
                                <Button variant="danger" loading={busy} onClick={() => patchStatus(CONSOLE_STATUS_CANCELED)}>
                                    {`${label('wms.common.cancel')} ${label('wms.service_order.field.code')}`}
                                </Button>
                                <Button variant="ghost" disabled={busy} onClick={() => setConfirmingCancel(false)}>
                                    {label('wms.location_create.back')}
                                </Button>
                            </>
                        ) : (
                            <Button variant="secondary" disabled={busy} onClick={() => setConfirmingCancel(true)}>
                                <X size={16} className="mr-1.5" />
                                {`${label('wms.common.cancel')} ${label('wms.service_order.field.code')}`}
                            </Button>
                        )
                    ) : null}

                    {routeItems.length > 0 ? (
                        <Button variant="ghost" onClick={() => setShowRoute((previous) => !previous)}>
                            <ChartLine size={16} className="mr-1.5" />
                            {label('wms.route.title')}
                        </Button>
                    ) : null}

                    <span className="flex-1" />
                    <Button variant="secondary" onClick={onClose}>
                        {label('wms.common.close')}
                    </Button>
                </div>
            }
        >
            {loading ? (
                <SkeletonList rows={4} />
            ) : loadError ? (
                <p className="text-sm text-danger-700 dark:text-danger-400">{loadError}</p>
            ) : order ? (
                <div className="space-y-6">
                    {actionError ? (
                        <p className="rounded-lg border border-danger-300 bg-danger-soft p-3 text-sm text-danger-800 dark:border-danger-800 dark:bg-danger-900/20 dark:text-danger-300">
                            {actionError}
                        </p>
                    ) : null}

                    <div className="grid gap-6 md:grid-cols-2">
                        <dl>
                            <SummaryRow term={label('wms.field.status')}>
                                <Badge variant={ServiceOrderStatusVariant(order.status)}>
                                    {label(`wms.service_order.status.${order.status}`)}
                                </Badge>
                            </SummaryRow>
                            <SummaryRow term={label('wms.service_order.field.type')}>
                                {order.type_label || order.type_code || BLANK}
                            </SummaryRow>
                            <SummaryRow term={label('wms.service_order.field.operator')}>
                                {order.operator_name ? (
                                    <>
                                        {order.operator_name}
                                        {order.operator_code ? (
                                            <Id value={order.operator_code} className="ml-1 text-xs text-mono-400" />
                                        ) : null}
                                    </>
                                ) : (
                                    BLANK
                                )}
                            </SummaryRow>
                            <SummaryRow term={label('wms.service_order.field.equipment')}>
                                {order.equipment_code ? <Id value={order.equipment_code} className="text-sm" /> : BLANK}
                            </SummaryRow>
                            <SummaryRow term={label('wms.service_order.field.route_algorithm')}>
                                {order.route_algorithm ? label(`wms.route.algorithm.${order.route_algorithm}`) : BLANK}
                            </SummaryRow>
                        </dl>

                        <dl>
                            <SummaryRow term={label('wms.lpn.field.created_at')}>
                                <DateTimeText value={order.created_at} className="text-sm" />
                            </SummaryRow>
                            <SummaryRow term={label('wms.service_order.status.READY')}>
                                {order.ready_at ? <DateTimeText value={order.ready_at} className="text-sm" /> : BLANK}
                            </SummaryRow>
                            {/* `accepted_at` é o instante em que a OS entrou em
                                execução (o aceite first-wins do coletor) — daí
                                o rótulo ser o próprio status. */}
                            <SummaryRow term={label('wms.service_order.status.IN_PROGRESS')}>
                                {order.accepted_at ? (
                                    <DateTimeText value={order.accepted_at} className="text-sm" />
                                ) : (
                                    BLANK
                                )}
                            </SummaryRow>
                            <SummaryRow
                                term={
                                    isCanceled
                                        ? label('wms.service_order.status.CANCELED')
                                        : label('wms.service_order.status.DONE')
                                }
                            >
                                {order.finished_at ? (
                                    <DateTimeText value={order.finished_at} className="text-sm" />
                                ) : (
                                    BLANK
                                )}
                            </SummaryRow>
                        </dl>
                    </div>

                    {/* Bloco de tempo. O rótulo muda com o desfecho: trabalho
                        interrompido não pode ser lido como tempo de execução. */}
                    {timing?.accepted ? (
                        <section
                            className={`rounded-lg border p-4 ${
                                isCanceled
                                    ? 'border-danger-300 bg-danger-soft dark:border-danger-800 dark:bg-danger-900/20'
                                    : 'border-mono-200 bg-mono-50 dark:border-mono-800 dark:bg-mono-950'
                            }`}
                        >
                            <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">
                                {isCanceled
                                    ? label('wms.service_order.status.CANCELED')
                                    : label('wms.service_order.field.sla')}
                            </h3>
                            {/* Dois números, não um: o tempo que valeu como
                                trabalho e o que ficou parado. Só o primeiro
                                esconderia meia hora de OS encostada. */}
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">
                                        {isCanceled
                                            ? label('wms.service_order.status.CANCELED')
                                            : label('wms.service_order.field.sla')}
                                    </p>
                                    <p className="font-mono text-xl font-semibold tabular-nums text-black dark:text-white">
                                        {FormatDurationSec(isCanceled ? timing.elapsed_seconds : timing.seconds)}
                                        {timing.running ? ' …' : ''}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-mono-500 dark:text-mono-400">
                                        {label('wms.service_order.status.PAUSED')}
                                    </p>
                                    <p className="font-mono text-xl font-semibold tabular-nums text-mono-700 dark:text-mono-300">
                                        {FormatDurationSec(timing.paused_seconds)}
                                        {timing.open_pause_count > 0 ? ' …' : ''}
                                    </p>
                                </div>
                            </div>
                            {/* As pausas são a ABERTURA do número acima, não
                                uma seção à parte: cada linha explica um pedaço
                                do tempo que saiu da conta do SLA. */}
                            {(order.pauses ?? []).length > 0 ? (
                                <ul className="mt-3 space-y-2">
                                    {order.pauses.map((pause) => (
                                        <li
                                            key={pause.id}
                                            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-mono-200 bg-white p-2 text-sm dark:border-mono-800 dark:bg-mono-950"
                                        >
                                            <Badge variant="warning">
                                                {label(`wms.pause.reason.${pause.reason}`)}
                                            </Badge>
                                            <DateTimeText value={pause.paused_at} className="text-xs" />
                                            <span className="text-mono-400">→</span>
                                            {pause.resumed_at ? (
                                                <DateTimeText value={pause.resumed_at} className="text-xs" />
                                            ) : (
                                                <span className="text-xs text-warning-700 dark:text-warning-400">
                                                    {label('wms.service_order.status.PAUSED')}
                                                </span>
                                            )}
                                            <span className="font-mono text-xs font-medium tabular-nums">
                                                {FormatDurationSec(PauseDurationSeconds(pause))}
                                                {pause.resumed_at ? '' : ' …'}
                                            </span>
                                            {pause.operator_name ? (
                                                <span className="text-xs text-mono-600 dark:text-mono-400">
                                                    {pause.operator_name}
                                                    {pause.operator_code ? (
                                                        <Id
                                                            value={pause.operator_code}
                                                            className="ml-1 text-[11px] text-mono-400"
                                                        />
                                                    ) : null}
                                                </span>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}

                            <p className="mt-3 text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.service_order.field.sla_help')}
                            </p>
                        </section>
                    ) : null}

                    {/* As outras OSs da JORNADA. Uma jornada dividida pode ter
                        mais de uma companheira; listar todas evita a promessa
                        de "par" que o modelo novo não faz mais. */}
                    {siblings.length > 0 ? (
                        <section className="rounded-lg border border-info-300 bg-info-soft p-3 dark:border-info-800 dark:bg-info-900/20">
                            <p className="text-sm text-info-900 dark:text-info-200">
                                {label('wms.service_order.group_notice')}
                            </p>
                            {siblings.map((entry) => (
                                <p key={entry.id} className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                                    <Id value={entry.code} className="font-semibold" />
                                    <Badge variant={ServiceOrderStatusVariant(entry.status)}>
                                        {label(`wms.service_order.status.${entry.status}`)}
                                    </Badge>
                                    <span className="text-mono-600 dark:text-mono-400">
                                        {entry.type_label || entry.type_code}
                                    </span>
                                </p>
                            ))}
                        </section>
                    ) : null}

                    <section className="space-y-2">
                        <h3 className="text-sm font-semibold text-black dark:text-white">
                            {label('wms.service_order.field.items')}
                        </h3>
                        <StandardTable
                            variant="minimal"
                            rows={order.items ?? []}
                            columns={itemColumns}
                            rowKey={(row) => row.id}
                            emptyMessage={label('wms.service_orders.empty')}
                            countLabel=""
                        />
                    </section>

                    {order.notes ? (
                        <section className="space-y-1">
                            <h3 className="text-sm font-semibold text-black dark:text-white">
                                {label('wms.field.notes')}
                            </h3>
                            <p className="whitespace-pre-line text-sm text-mono-700 dark:text-mono-300">
                                {order.notes}
                            </p>
                        </section>
                    ) : null}

                    {/* Rota SÓ leitura: o algoritmo fica gravado na OS na
                        criação e o PATCH do console não o altera — oferecer
                        "usar esta rota" aqui prometeria o que o contrato não
                        cumpre. O desenho serve para o Supervisor SABER por
                        onde o operador vai passar. */}
                    {showRoute && routeItems.length > 0 ? (
                        <RouteComparator
                            items={routeItems}
                            label={label}
                            equipmentCode={order.equipment_code}
                            chosenAlgorithm={order.route_algorithm}
                            readOnly
                        />
                    ) : null}
                </div>
            ) : null}
        </Modal>
    );
};

export default ServiceOrderDetailModal;
